import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';
import { ArrowLeft, Package, User, MapPin, Phone, Mail, FileText, Truck, ClipboardList } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';
import { Order } from '../types';

const getStatusColor = (status: Order['status']) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
    case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { getMyOrder, language } = useStore();
  const t = TRANSLATIONS;

  const order = orderId ? getMyOrder(orderId) : undefined;

  useEffect(() => {
    if (order) {
      updateMetaTags({
        title: `${t.orderDetails[language]} ${order.id} - ${SEO_CONFIG.storeName}`,
        description: language === 'en' ? `Order details for ${order.id}` : `Szczegóły zamówienia ${order.id}`,
        canonical: getFullUrl(`/orders/${order.id}`),
      });
    }
  }, [order, language, t]);

  if (!order) {
    return (
      <main className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <ClipboardList className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t.orderNotFound[language]}</h2>
        <p className="text-gray-500 mb-6">
          {language === 'en'
            ? 'The order you are looking for does not exist or has been removed.'
            : 'Zamówienie, którego szukasz, nie istnieje lub zostało usunięte.'}
        </p>
        <Link
          to="/orders"
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-8 rounded-xl transition-all shadow-lg shadow-emerald-200 flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          {t.backToOrders[language]}
        </Link>
      </main>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <main className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <Link
            to="/orders"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToOrders[language]}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {t.orderNumber[language]}{order.id}
          </h1>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border self-start ${getStatusColor(order.status)}`}>
          {t[order.status]?.[language] || order.status}
        </span>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              {t.orderDetails[language]}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-500">{t.orderDate[language]}</p>
                  <p className="font-medium text-gray-900">{formatDate(order.date)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-500">{t.name[language]}</p>
                  <p className="font-medium text-gray-900">{order.customer.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-500">{t.email[language]}</p>
                  <p className="font-medium text-gray-900">{order.customer.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-500">{t.phone[language]}</p>
                  <p className="font-medium text-gray-900">{order.customer.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:col-span-2">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-500">{t.address[language]}</p>
                  <p className="font-medium text-gray-900">
                    {order.customer.address}, {order.customer.city} {order.customer.zip}
                  </p>
                </div>
              </div>
              {order.customer.taxNumber && (
                <div className="flex items-start gap-3 sm:col-span-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-gray-500">{t.taxNumber[language]}</p>
                    <p className="font-medium text-gray-900">{order.customer.taxNumber}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {language === 'en' ? 'Items' : 'Produkty'}
            </h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="flex flex-col sm:flex-row items-start gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                >
                  <div className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80?text=No+Image';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t.quantity[language]}: {item.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{(item.price * item.quantity).toFixed(2)} zł</p>
                    <p className="text-sm text-gray-500">{item.price.toFixed(2)} zł / {language === 'en' ? 'pc' : 'szt'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {language === 'en' ? 'Summary' : 'Podsumowanie'}
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>{t.subtotal[language]}</span>
                <span>{order.subtotal.toFixed(2)} zł</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>{t.vat[language]}</span>
                <span>{order.vat.toFixed(2)} zł</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="font-bold text-gray-900 text-base">{t.totalGross[language]}</span>
                <span className="font-bold text-emerald-600 text-xl">{order.total.toFixed(2)} zł</span>
              </div>
            </div>
          </div>
        </div>

        {(order.trackingNumber || order.notes) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 md:p-6 space-y-4">
              {order.trackingNumber && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">{t.trackingNumber[language]}</p>
                    <p className="font-medium text-gray-900">{order.trackingNumber}</p>
                  </div>
                </div>
              )}
              {order.notes && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">{t.notes[language]}</p>
                    <p className="font-medium text-gray-900">{order.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Link
            to="/orders"
            className="flex-1 bg-white border-2 border-gray-200 hover:border-emerald-500 text-gray-700 hover:text-emerald-600 font-bold py-3 px-6 rounded-xl transition-all text-center flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            {t.backToOrders[language]}
          </Link>
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
          >
            <Package className="w-5 h-5" />
            {t.continueShopping[language]}
          </button>
        </div>
      </div>
    </main>
  );
};
